import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/lib/toast';
import { useDataStore, type DataState } from '@/stores/dataStore';
import { parseMessageLink } from '@/lib/messageLink';
import { emitAppEvent } from '@/lib/appEvents';

/**
 * Handles shareable message links (see lib/messageLink). On first load, and
 * whenever the address changes afterwards (`popstate` — the platform shell's
 * `cispracs:deep-link` rewrites the frame URL and fires one), if the URL is
 * /channels/{g}/{c}/{m} or /dm/{c}/{m}, jumps to that channel and scrolls to
 * the message (reusing the existing `chat:scroll-to-message` pipeline), then
 * cleans the URL back to "/".
 *
 * Waits for the store to hydrate (guilds / DMs arrive from /api/me) through a
 * one-off store subscription that ends once the link is resolved. It used to
 * subscribe to every guild and DM from the app shell, so each DM or guild
 * update re-rendered the whole shell for a link handled once.
 *
 * A message link is a pure pointer: it never admits anyone. If the viewer
 * isn't a member (or can't see the channel), they get a clear "you need an
 * invite" message — bringing someone in is the explicit «Пригласить» action.
 */
export function useMessageDeepLink() {
  const { t } = useTranslation();

  useEffect(() => {
    // Waiting subscription of the link being resolved; a newer link replaces it.
    let pending: (() => void) | null = null;
    const stopPending = () => {
      pending?.();
      pending = null;
    };

    const handle = () => {
      const target = parseMessageLink(window.location.pathname);
      if (!target) return;
      stopPending();
      let done = false;

      const finish = () => {
        done = true;
        window.history.replaceState({}, '', '/');
      };
      // Mark the link handled BEFORE switching: selecting writes to the store,
      // which notifies the subscription below synchronously.
      const open = (select: () => void) => {
        finish();
        select();
        requestAnimationFrame(() => emitAppEvent('chat:scroll-to-message', { messageId: target.messageId }));
      };
      const resolve = (s: DataState) => {
        // Either map being non-empty means /api/me has hydrated the store.
        const loaded = Object.keys(s.guilds).length > 0 || Object.keys(s.dmChannels).length > 0;
        if (target.kind === 'guild' && target.guildId) {
          const guild = s.guilds[target.guildId];
          if (guild) {
            if (guild.channels.some((c) => c.id === target.channelId)) {
              const guildId = target.guildId;
              open(() => {
                s.selectGuild(guildId);
                s.selectChannel(target.channelId);
              });
            } else {
              finish();
              toast.error(t('deepLink.channelUnavailable'));
            }
          } else if (loaded) {
            finish();
            toast.error(t('deepLink.notMember'));
          }
        } else if (target.kind === 'dm') {
          if (s.dmChannels[target.channelId]) {
            open(() => s.selectDirectChannel(target.channelId));
          } else if (loaded) {
            finish();
            toast.error(t('deepLink.dmUnavailable'));
          }
        }
      };

      resolve(useDataStore.getState());
      if (done) return;
      const unsubscribe = useDataStore.subscribe((s) => {
        if (done) return;
        resolve(s);
        if (done) stopPending();
      });
      pending = unsubscribe;
    };

    handle();
    window.addEventListener('popstate', handle);
    return () => {
      window.removeEventListener('popstate', handle);
      stopPending();
    };
  }, [t]);
}
